import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { TieredMenu } from "primereact/tieredmenu";
import { MenuItem } from "primereact/menuitem";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Skeleton } from "primereact/skeleton";
import { StatusIcon } from "./StatusIcon";
import styles from "./ReportActionMenu.module.css";
import {
  useAddComment,
  useClearanceRecipients,
  useDeleteReport,
  useUpdateReport,
  useUsers,
} from "../data";
import * as client from "../../../client";
import { PantheonAvatarWithoutMenu } from "../../../components/avatar/Avatar";
import { displayNameForUser, isAutomationAccount } from "../../../util";
import { SafeReportMetadataResponse } from "../Conversion";
import { useClientId, useFeaturesForClientId } from "../../../ClientConfig";
import { featureEnabled } from "../../../client-features";
import { confirmDialog } from "primereact/confirmdialog";
import { sendClearanceEmail } from "../../../api/routes";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../routes";
import { getAllStatuses, isClearanceStatus } from "./util";
import { useToast } from "../../../context-providers/Toast";

type ReportActionMenuProps = {
  report: SafeReportMetadataResponse;
  isUpcomingRenewal?: boolean;
  setActionMenuIsOpen?: (value: boolean) => void;
  raised?: boolean;
  onCreateBor?: () => void;
  onManageBor?: () => void;
  // Navigate to the default route after a successful delete. Used on the
  // report detail page (where the report disappears out from under us);
  // unset on the dashboard, where invalidating the list is enough.
  navigateAfterDelete?: boolean;
};

const INTERNAL_DOMAIN = "@luminorrisk.com";

const isExternalEmail = (email: string): boolean => {
  return !email.toLowerCase().endsWith(INTERNAL_DOMAIN);
};

const userListWithSeparators = (
  allUsers: client.User[],
  title: string,
  filterLogic: (user: client.User) => boolean,
  onSelect: (userId: string | null) => void
) => {
  const humans = allUsers.filter((u) => !isAutomationAccount(u));
  const underwriters = humans.filter(filterLogic);
  const others = humans.filter((user) => !filterLogic(user));

  const items: MenuItem[] = [];

  // Add underwriters section
  if (underwriters.length > 0) {
    items.push({
      label: title,
      disabled: true,
      className: styles.header,
    });
  }
  items.push(
    ...underwriters.map((user) => ({
      icon: (
        <PantheonAvatarWithoutMenu
          label={displayNameForUser(user)}
          imageUrl={user.profile_image_url}
          showName={true}
        />
      ),
      command: () => {
        onSelect(user.id);
      },
    }))
  );

  // Add separator if both sections exist
  if (underwriters.length > 0 && others.length > 0) {
    items.push({ separator: true });
  }

  // Add others section (including null option)
  if (others.length > 0) {
    items.push({
      label: "Other Users",
      disabled: true,
      className: styles.header,
    });
  }

  items.push(
    ...others.map((user) => ({
      icon: (
        <PantheonAvatarWithoutMenu
          label={displayNameForUser(user)}
          imageUrl={user.profile_image_url}
          showName={true}
        />
      ),
      command: () => {
        onSelect(user.id);
      },
    }))
  );

  // Add null option to others section
  items.push({
    icon: (
      <PantheonAvatarWithoutMenu
        label={undefined}
        imageUrl={null}
        showName={true}
      />
    ),
    command: () => {
      onSelect(null);
    },
  });

  return items;
};

// Loose client-side check: backend is the source of truth for validation.
const looksLikeEmail = (value: string): boolean =>
  /^[^@\s]+@[^@\s]+$/.test(value);

type RecipientRowProps = {
  email: string;
  locked: boolean;
  disabled: boolean;
  onRemove: () => void;
};

const RecipientRow: React.FC<RecipientRowProps> = ({
  email,
  locked,
  disabled,
  onRemove,
}) => {
  const external = isExternalEmail(email);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        height: "24px",
      }}
    >
      <i
        className={external ? "pi pi-globe" : "pi pi-building"}
        style={{
          fontSize: "12px",
          color: external ? "var(--orange-500)" : "var(--green-500)",
        }}
      />
      <span style={{ flexGrow: 1 }}>{email}</span>
      {external && (
        <span
          style={{
            fontSize: "12px",
            fontWeight: "bold",
            color: "var(--orange-500)",
            backgroundColor: "var(--surface-100)",
            padding: "1px 6px",
            borderRadius: "4px",
          }}
        >
          External
        </span>
      )}
      {!locked && (
        <i
          className="pi pi-trash"
          role="button"
          aria-label={`Remove ${email}`}
          tabIndex={disabled ? -1 : 0}
          style={{
            fontSize: "14px",
            color: disabled ? "var(--surface-400)" : "var(--red-500)",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
          onClick={() => {
            if (disabled) return;
            confirmDialog({
              message: `Remove ${email} from the recipient list?`,
              header: "Remove recipient",
              acceptLabel: "Remove",
              acceptClassName: "p-button-danger",
              rejectLabel: "Cancel",
              defaultFocus: "reject",
              accept: onRemove,
            });
          }}
        />
      )}
    </div>
  );
};

type ClearanceEmailDialogProps = {
  visible: boolean;
  onHide: () => void;
  reportId: string;
  status: client.status;
};

const ClearanceEmailDialog: React.FC<ClearanceEmailDialogProps> = ({
  visible,
  onHide,
  reportId,
  status,
}) => {
  const toast = useToast();
  const updateReport = useUpdateReport(reportId);
  const [newEmail, setNewEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [addPending, setAddPending] = useState(false);

  const recipientsQuery = useClearanceRecipients(reportId, visible);

  const recipients = recipientsQuery.data?.recipients ?? [];
  const lockedEmails = recipientsQuery.data?.locked_emails ?? [];
  const editable = recipients.filter((e) => !lockedEmails.includes(e));
  const mutating = updateReport.isPending || recipientsQuery.isFetching;

  const persistRecipients = (nextEditable: string[]) => {
    updateReport.mutate(
      { submissionRecipients: nextEditable },
      {
        onError: () => {
          toast.show({
            severity: "error",
            summary: "Failed to save recipients",
          });
        },
      }
    );
  };

  const removeRecipient = (email: string) => {
    persistRecipients(editable.filter((r) => r !== email));
  };

  const addRecipient = () => {
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    if (recipients.some((r) => r.toLowerCase() === trimmed.toLowerCase())) {
      setNewEmail("");
      return;
    }
    setNewEmail("");
    setAddPending(true);
    persistRecipients([...editable, trimmed]);
  };

  // Clear the add-placeholder once the mutation and the follow-up refetch
  // have both settled — at that point the new row is already rendered from
  // the refetched query data.
  useEffect(() => {
    if (addPending && !mutating) setAddPending(false);
  }, [addPending, mutating]);

  const send = async () => {
    setSending(true);
    try {
      await sendClearanceEmail(reportId, status);
      toast.show({
        severity: "success",
        summary: "Email Sent",
        detail: `Clearance email sent to ${recipients.length} recipient(s)`,
      });
      onHide();
    } catch {
      toast.show({
        severity: "error",
        summary: "Email Failed",
        detail: "Failed to send clearance email",
      });
    } finally {
      setSending(false);
    }
  };

  const addDisabled =
    !newEmail.trim() || !looksLikeEmail(newEmail.trim()) || mutating;

  return (
    <Dialog
      header="Send Clearance Email"
      visible={visible}
      onHide={() => {
        if (sending) return;
        onHide();
      }}
      style={{ width: "480px" }}
      footer={
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
        >
          <Button
            label="Cancel"
            severity="secondary"
            text
            onClick={onHide}
            disabled={sending}
          />
          <Button
            label="Send"
            onClick={send}
            loading={sending}
            disabled={mutating || recipients.length === 0}
          />
        </div>
      }
    >
      {recipientsQuery.isPending ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height="24px" />
          ))}
        </div>
      ) : recipients.length === 0 ? (
        <div style={{ color: "#888", fontStyle: "italic", padding: "8px 0" }}>
          No recipients found for this submission.
        </div>
      ) : (
        <div>
          <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
            This email will be sent to:
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {recipients.map((email) => (
              <RecipientRow
                key={email}
                email={email}
                locked={lockedEmails.includes(email)}
                disabled={mutating}
                onRemove={() => removeRecipient(email)}
              />
            ))}
            {addPending && <Skeleton height="24px" />}
          </div>
        </div>
      )}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginTop: "16px",
          alignItems: "center",
        }}
      >
        <InputText
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !addDisabled) {
              e.preventDefault();
              addRecipient();
            }
          }}
          placeholder="Add an email..."
          style={{ flexGrow: 1 }}
          disabled={mutating || sending}
        />
        <Button
          icon="pi pi-plus"
          label="Add"
          size="small"
          onClick={addRecipient}
          disabled={addDisabled || sending}
        />
      </div>
    </Dialog>
  );
};

// Copy for the reason prompt, keyed by the status that triggers it. Both "lost"
// and "closed" capture a free-text reason that is stored as a report comment.
const REASON_DIALOG_COPY: Partial<
  Record<client.status, { header: string; prompt: string }>
> = {
  lost: {
    header: "Reason for not taking up",
    prompt: "Why was this deal not taken up?",
  },
  closed: {
    header: "Reason for not quoting",
    prompt: "Why was this submission reviewed but not quoted?",
  },
};

const ReasonDialog: React.FC<{
  status: client.status | null;
  onHide: () => void;
  onSave: (reason: string) => void;
  saving: boolean;
}> = ({ status, onHide, onSave, saving }) => {
  const [value, setValue] = useState("");
  const copy = status ? REASON_DIALOG_COPY[status] : undefined;
  const handleHide = () => {
    setValue("");
    onHide();
  };
  const handleSave = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      handleHide();
      return;
    }
    onSave(trimmed);
    setValue("");
  };
  return (
    <Dialog
      header={copy?.header ?? ""}
      visible={copy !== undefined}
      onHide={handleHide}
      style={{ width: "480px" }}
      footer={
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}
        >
          <Button
            label="Skip"
            severity="secondary"
            text
            onClick={handleHide}
            disabled={saving}
          />
          <Button label="Save" onClick={handleSave} loading={saving} />
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div>{copy?.prompt}</div>
        <InputTextarea
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={4}
          placeholder="Enter a reason..."
          style={{ width: "100%", boxSizing: "border-box" }}
        />
      </div>
    </Dialog>
  );
};

export const useReportActionMenuItems = (
  report: SafeReportMetadataResponse | null,
  isUpcomingRenewal?: boolean,
  onCreateBor?: () => void,
  onManageBor?: () => void,
  navigateAfterDelete?: boolean
): { items: MenuItem[]; dialogs: React.ReactNode } => {
  const clientId = useClientId();
  const users = useUsers().data;
  const lobUsers =
    users?.all_company_users.filter((u) =>
      report ? u.lobs.includes(report.lob) : false
    ) ?? [];
  const navigate = useNavigate();
  const features = useFeaturesForClientId(clientId, navigate);
  const updateReport = useUpdateReport(report?.report_id ?? "");
  const addComment = useAddComment(report?.report_id ?? "");
  const deleteReport = useDeleteReport();
  const toast = useToast();
  const [reasonDialogStatus, setReasonDialogStatus] =
    useState<client.status | null>(null);
  const [clearanceDialogStatus, setClearanceDialogStatus] =
    useState<client.status | null>(null);

  const items = useMemo(() => {
    if (!report) return [];
    const baseItems: MenuItem[] = [
      {
        label: "Update status",
        icon: "pi pi-file-edit",
        items: getAllStatuses().map(
          (status): MenuItem => ({
            icon: <StatusIcon status={status} />,
            command: async () => {
              await updateReport.mutateAsync({
                status: { new_status: status },
              });
              if (isClearanceStatus(status)) {
                setClearanceDialogStatus(status);
              }
              if (status === "lost" || status === "closed") {
                setReasonDialogStatus(status);
              }
            },
          })
        ),
      },
      {
        label: featureEnabled(clientId, "assignee-named-underwriter")
          ? "Update underwriter"
          : "Update assignee",
        icon: "pi pi-user-edit",
        items: featureEnabled(clientId, "underwriter-handler-separation")
          ? userListWithSeparators(
              lobUsers,
              "Underwriters",
              () => true,
              (userId) =>
                updateReport.mutate({
                  assignee: { new_assignee: userId },
                })
            )
          : (lobUsers as (client.User | null)[])
              .concat([null])
              // null is the "unassign" option — keep it.
              .filter((u) => u === null || !isAutomationAccount(u))
              .map((user) => {
                const label = user ? displayNameForUser(user) : undefined;
                return {
                  icon: (
                    <PantheonAvatarWithoutMenu
                      label={label}
                      imageUrl={user?.profile_image_url}
                      showName={true}
                    />
                  ),
                  command: () => {
                    updateReport.mutate({
                      assignee: { new_assignee: user ? user.id : null },
                    });
                  },
                };
              }),
      },
      ...(featureEnabled(clientId, "handler-column")
        ? [
            {
              label: "Update specialist",
              icon: "pi pi-users",
              items: featureEnabled(clientId, "underwriter-handler-separation")
                ? userListWithSeparators(
                    lobUsers,
                    "Specialists",
                    () => true,
                    (userId) =>
                      updateReport.mutate({
                        additionalAssignees: {
                          assignees: userId ? { handler: userId } : {},
                        },
                      })
                  )
                : (lobUsers as (client.User | null)[])
                    .concat([null])
                    .map((user) => {
                      const label = user ? displayNameForUser(user) : undefined;
                      return {
                        icon: (
                          <PantheonAvatarWithoutMenu
                            label={label}
                            imageUrl={user?.profile_image_url}
                            showName={true}
                          />
                        ),
                        command: () => {
                          updateReport.mutate({
                            additionalAssignees: {
                              assignees: user ? { handler: user.id } : {},
                            },
                          });
                        },
                      };
                    }),
            },
          ]
        : []),
    ];

    if (onManageBor) {
      baseItems.push({
        label: "Manage BOR",
        icon: "pi pi-arrows-h",
        command: () => onManageBor(),
      });
    } else if (onCreateBor) {
      baseItems.push({
        label: "Create BOR letter",
        icon: "pi pi-arrows-h",
        command: () => onCreateBor(),
      });
    }

    if (users?.current_user.is_client_admin) {
      if (
        featureEnabled(clientId, "submission-history", {
          user: users.current_user,
        })
      ) {
        baseItems.push({
          label: "View history",
          icon: "pi pi-history",
          command: () => {
            navigate(
              ROUTES.DASHBOARD_SUBMISSION_HISTORY_DETAIL(report.report_id)
            );
          },
        });
      }
      baseItems.push({
        label: "Delete report",
        icon: "pi pi-trash",
        command: () =>
          confirmDialog({
            message: "Are you sure you want to delete this report?",
            header: "Delete Report",
            defaultFocus: "reject",
            acceptClassName: "p-button-danger",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            accept: async () => {
              await deleteReport.mutateAsync(report.report_id);
              if (navigateAfterDelete) {
                navigate(features.defaultRoute(users.current_user));
              }
              toast.show({
                severity: "success",
                summary: `${report.report_metadata.company_info.company_name} deleted`,
              });
            },
          }),
      });
    }

    return baseItems;
  }, [users, updateReport, isUpcomingRenewal, navigateAfterDelete]);

  const dialogs = (
    <>
      <ReasonDialog
        status={reasonDialogStatus}
        onHide={() => setReasonDialogStatus(null)}
        saving={addComment.isPending}
        onSave={async (reason) => {
          try {
            await addComment.mutateAsync(reason);
            setReasonDialogStatus(null);
          } catch {
            toast.show({
              severity: "error",
              summary: "Failed to save reason",
            });
          }
        }}
      />
      {report && clearanceDialogStatus && (
        <ClearanceEmailDialog
          visible={clearanceDialogStatus !== null}
          onHide={() => setClearanceDialogStatus(null)}
          reportId={report.report_id}
          status={clearanceDialogStatus}
        />
      )}
    </>
  );

  return { items, dialogs };
};

export const ReportActionMenu: React.FC<ReportActionMenuProps> = ({
  report,
  isUpcomingRenewal,
  setActionMenuIsOpen,
  raised = false,
  onCreateBor,
  onManageBor,
  navigateAfterDelete,
}) => {
  const menu = useRef<TieredMenu>(null);
  const { items, dialogs } = useReportActionMenuItems(
    report,
    isUpcomingRenewal,
    onCreateBor,
    onManageBor,
    navigateAfterDelete
  );

  return (
    // Stop click propagation: PrimeReact portals its menu / dialogs to
    // document.body but React events still bubble through the React tree,
    // which would otherwise trigger the dashboard row's row-click navigation.
    <div onClick={(e) => e.stopPropagation()}>
      <TieredMenu
        model={items}
        popup
        ref={menu}
        style={{ width: "232px" }}
        pt={{ submenu: { style: { maxHeight: "600px", overflowY: "auto" } } }}
        breakpoint="767px"
        onShow={() => setActionMenuIsOpen && setActionMenuIsOpen(true)}
        onHide={() => setActionMenuIsOpen && setActionMenuIsOpen(false)}
      />
      <Button
        text
        raised={raised}
        severity="secondary"
        icon="pi pi-ellipsis-v"
        onClick={(e) => {
          menu.current?.toggle(e);
        }}
      />
      {dialogs}
    </div>
  );
};
